import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import { useSmoothScroll } from '../../hooks/useSmoothScroll';

export default function CompanyPage() {
  const [mapLoaded, setMapLoaded] = useState(false);
  useSmoothScroll();

  useSEO({
    title: '会社概要｜京都・滋賀の格安防犯カメラ販売・設置工事｜ネクストステージ',
    description: 'ネクストステージの会社概要。京都府・滋賀県専門の格安防犯カメラ販売・設置工事サービス。大阪府大阪市中央区東心斎橋に拠点を置き、京都・滋賀全域に対応。',
    keywords: '防犯カメラ 京都 格安, 防犯カメラ 京都, 防犯カメラ 滋賀 格安, 防犯カメラ 滋賀',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/company`,
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/company`,
          name: '会社概要｜京都・滋賀の格安防犯カメラ販売・設置工事｜ネクストステージ',
          description: 'ネクストステージの会社概要。京都府・滋賀県専門の格安防犯カメラ販売・設置工事サービス。',
          inLanguage: 'ja',
          isPartOf: {
            '@type': 'WebSite',
            '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
            url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
            name: 'ネクストステージ',
            inLanguage: 'ja'
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'ホーム',
                item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '会社概要',
                item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/company`
              }
            ]
          }
        },
        {
          '@type': 'Organization',
          '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`,
          name: 'Next stage（ネクストステージ）',
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/b20bf12da98b13bf6586d3e638cf3b35.jpeg'
          },
          telephone: '080-2527-8110',
          address: {
            '@type': 'PostalAddress',
            postalCode: '542-0083',
            addressCountry: 'JP',
            addressRegion: '大阪府',
            addressLocality: '大阪市中央区',
            streetAddress: '東心斎橋１丁目９-１９-３０７'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 34.6937,
            longitude: 135.5023
          },
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '17:00'
          },
          areaServed: [
            { '@type': 'State', name: '京都府' },
            { '@type': 'State', name: '滋賀県' },
            { '@type': 'State', name: '大阪府' },
            { '@type': 'State', name: '奈良県' },
            { '@type': 'State', name: '兵庫県' },
            { '@type': 'State', name: '和歌山県' }
          ],
          description: '京都府・滋賀県専門の格安防犯カメラ販売・設置工事サービス。地域密着で安心の施工を提供。',
          sameAs: []
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="bg-teal-700 text-white">
          <nav className="max-w-7xl mx-auto px-4">
            <ul className="flex flex-wrap items-center justify-center gap-6 py-3 text-sm">
              <li>
                <Link to="/#camera-features" className="hover:text-teal-200 whitespace-nowrap">実績の一部紹介</Link>
              </li>
              <li>
                <Link to="/#achievements" className="hover:text-teal-200 whitespace-nowrap">今どきの防犯カメラの機能</Link>
              </li>
              <li>
                <Link to="/#use-cases" className="hover:text-teal-200 whitespace-nowrap">防犯カメラ活用事例</Link>
              </li>
              <li>
                <Link to="/#faq-section" className="hover:text-teal-200 whitespace-nowrap">よくあるご質問(Q&A)</Link>
              </li>
              <li>
                <a href="#company-info" className="hover:text-teal-200 whitespace-nowrap">会社概要</a>
              </li>
            </ul>
          </nav>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-3">
                <img 
                  src="https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/b20bf12da98b13bf6586d3e638cf3b35.jpeg" 
                  alt="Next stage ロゴ" 
                  className="h-12 w-auto object-contain"
                  fetchPriority="high"
                />
                <span className="text-2xl font-bold text-teal-700 m-0 p-0 leading-none">京都・滋賀の格安防犯カメラ販売・設置工事</span>
              </Link>
            </div>
            
            <div className="hidden lg:flex items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <a
                    href="tel:08025278110"
                    className="text-2xl font-bold text-teal-700"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    TEL. 080-2527-8110
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 mt-32 lg:mt-28">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm text-gray-600">
          <Link to="/" className="hover:text-gray-900">ホーム</Link>
          <span className="mx-2">&gt;</span>
          <span className="text-gray-900">会社概要</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-12">
        <h1 id="company-info" className="text-2xl font-bold text-gray-900 mb-12 text-center">
          京都・滋賀の格安防犯カメラ設置工事｜会社概要
        </h1>

        <div className="mb-12 rounded-2xl overflow-hidden shadow-lg">
          <img 
            src="https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/17cbf20c723d4fe5ad992539414bf48f.jpeg" 
            alt="会社外観" 
            className="w-full h-96 object-cover object-top"
            loading="lazy"
            decoding="async"
            width="1200"
            height="384"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
          <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
              <i className="ri-building-line text-2xl text-teal-700"></i>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-600 mb-1">会社名</div>
              <div className="text-xl font-bold text-gray-900">Next stage（ネクストステージ）</div>
            </div>
          </div>

          <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
              <i className="ri-map-pin-line text-2xl text-teal-700"></i>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-600 mb-1">所在地</div>
              <div className="text-lg text-gray-900">〒542-0083<br />大阪府大阪市中央区東心斎橋１丁目９-１９-３０７</div>
            </div>
          </div>

          <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
              <i className="ri-phone-line text-2xl text-teal-700"></i>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-600 mb-1">お問い合わせ電話番号</div>
              <div className="text-xl font-bold text-teal-700">
                📞{' '}
                <a
                  href="tel:08025278110"
                  className="text-teal-700"
                  style={{ textDecoration: 'none' }}
                >
                  080-2527-8110
                </a>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
              <i className="ri-service-line text-2xl text-teal-700"></i>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-600 mb-1">事業内容</div>
              <div className="text-lg text-gray-900">京都府・滋賀県専門の格安防犯カメラ販売・設置工事・設定サポート</div>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">アクセス</h2>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="aspect-video relative">
              {!mapLoaded ? (
                <div
                  className="w-full h-full bg-gray-100 flex flex-col items-center justify-center cursor-pointer group"
                  onClick={() => setMapLoaded(true)}
                  style={{ minHeight: '400px' }}
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full shadow-md mb-4 group-hover:shadow-lg transition-shadow">
                    <i className="ri-map-2-line text-4xl text-teal-700"></i>
                  </div>
                  <p className="text-gray-700 font-bold text-lg mb-2">地図を表示する</p>
                  <p className="text-gray-500 text-sm">クリックするとGoogleマップが読み込まれます</p>
                  <div className="mt-4 px-6 py-2 bg-teal-700 text-white rounded-full text-sm font-bold group-hover:bg-teal-600 transition-colors whitespace-nowrap">
                    マップを開く
                  </div>
                </div>
              ) : (
                <iframe
                  src="https://maps.google.com/maps?q=大阪府大阪市中央区東心斎橋１丁目９-１９&output=embed&hl=ja&z=16"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="会社所在地マップ"
                ></iframe>
              )}
            </div>
            <div className="p-6 bg-gray-50">
              <p className="text-sm text-gray-600 text-center">
                大阪府大阪市中央区東心斎橋１丁目９-１９-３０７
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 pt-12 pb-6 mt-16">
        <div className="max-w-7xl mx-auto px-4 mb-12">
          <div className="bg-white p-6 rounded-lg flex items-center justify-center gap-6">
            <i className="ri-map-pin-line text-4xl text-red-600"></i>
            <div>
              <div className="font-bold text-lg">格安防犯カメラ設置 ご対応エリア</div>
              <div className="text-gray-700"><strong>京都府・滋賀県</strong>（大阪・奈良・兵庫・和歌山も対応）</div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 mb-12">
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-red-600 text-white p-6 rounded-lg flex items-center justify-center gap-4">
              <i className="ri-phone-line text-3xl"></i>
              <div>
                <a
                  href="tel:08025278110"
                  className="text-2xl font-bold text-white"
                  style={{ textDecoration: 'none' }}
                >
                  TEL. 080-2527-8110
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 pt-8 border-t border-gray-300">
          <div className="flex flex-col lg:flex-row justify-between gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/b20bf12da98b13bf6586d3e638cf3b35.jpeg" 
                  alt="Next stage ロゴ" 
                  className="h-8 w-auto object-contain"
                  loading="lazy"
                  decoding="async"
                  width="80"
                  height="32"
                />
                <div className="text-2xl font-bold text-teal-700">Next stage（ネクストステージ）</div>
              </div>
              <div className="text-sm text-gray-600">京都・滋賀の格安防犯カメラ設置で、家族と店舗に確かな安心を</div>
              <div className="text-sm text-gray-600">
                〒542-0083 大阪府大阪市中央区東心斎橋１丁目９-１９-３０７<br />
                <a
                  href="tel:08025278110"
                  className="text-gray-600"
                  style={{ textDecoration: 'none' }}
                >
                  TEL 080-2527-8110
                </a>
              </div>
            </div>
            <nav className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Link to="/#use-cases" className="hover:text-red-600 whitespace-nowrap">防犯カメラ活用事例</Link>
              <Link to="/#faq-section" className="hover:text-red-600 whitespace-nowrap">防犯に不安を感じ始めた方へ</Link>
              <a href="#company-info" className="hover:text-red-600 whitespace-nowrap">会社概要</a>
            </nav>
          </div>
          <div className="text-center text-sm text-gray-600 pt-4 border-t border-gray-300">
            © 2025 京都・滋賀の格安防犯カメラ設置工事サービス　ネクストステージ　｜　<a href="https://readdy.ai/?ref=logo" rel="nofollow" className="hover:underline">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
